import "bootstrap/dist/css/bootstrap.min.css";
import "animate.css";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchOrders, updateOrder } from "../store/ordersSlice.js";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import Bill from "./Bill.js";
import { downloadBillAsPDF } from "../utils/billUtils.js";
import { ref, update } from "firebase/database";
import { db } from "../firebase.js";

const Stars = ({ value, onChange, hover, onHover, onLeave, size = 32 }) => (
  <div className="d-flex justify-content-center gap-1">
    {[1, 2, 3, 4, 5].map((s) => (
      <i
        key={s}
        className={`bi bi-star${s <= (hover || value) ? "-fill" : ""} text-warning`}
        style={{ fontSize: size, cursor: onChange ? "pointer" : "default" }}
        onClick={() => onChange && onChange(s)}
        onMouseEnter={() => onHover && onHover(s)}
        onMouseLeave={() => onLeave && onLeave()}
      />
    ))}
  </div>
);

const fmt = (ts) =>
  ts ? new Date(ts).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : null;

const StatusCard = ({ order }) => {
  const s = order.status || "confirmed";

  if (s === "confirmed") return (
    <div className="alert alert-info border-0 rounded-3 mt-3 mb-0">
      <div className="fw-bold mb-1"><i className="bi bi-check-circle-fill me-2"></i>Order Confirmed!</div>
      <div className="small mb-1">Order ID: <span className="fw-bold font-monospace">{order.orderId || order.id}</span></div>
      <div className="small mb-1">Payment ID: <span className="fw-bold font-monospace">{order.paymentId}</span></div>
      <div className="small text-muted mt-2">
        <i className="bi bi-clock me-1"></i>
        Your order may take <strong>24–48 hours</strong> to be shipped. You will receive an update on your email once your product is shipped.
      </div>
    </div>
  );

  if (s === "placed") return (
    <div className="alert alert-primary border-0 rounded-3 mt-3 mb-0">
      <div className="fw-bold mb-1"><i className="bi bi-truck me-2"></i>Your Order Has Been Shipped!</div>
      <div className="small mb-2 text-muted">Your product is on its way. You can track it using the link below.</div>
      {order.trackingLink ? (
        <a href={order.trackingLink} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm rounded-pill">
          <i className="bi bi-box-arrow-up-right me-1"></i>Track Your Order
        </a>
      ) : (
        <div className="small text-muted"><i className="bi bi-info-circle me-1"></i>Tracking link will be updated shortly.</div>
      )}
      {order.trackingId && <div className="small mt-2">Tracking ID: <strong>{order.trackingId}</strong></div>}
      {order.courierPartner && <div className="small">Courier: <strong>{order.courierPartner}</strong></div>}
    </div>
  );

  if (s === "delivered") return (
    <div className="alert alert-success border-0 rounded-3 mt-3 mb-0">
      <div className="fw-bold mb-1"><i className="bi bi-emoji-smile me-2"></i>Delivered Successfully!</div>
      <div className="small">
        🎉 Thank you for shopping with <strong>AarnaStores</strong>! We hope you love your fragrance. Your satisfaction means everything to us. See you again soon! 💛
      </div>
    </div>
  );

  if (s === "cancellation_in_process") return (
    <div className="alert alert-warning border-0 rounded-3 mt-3 mb-0">
      <div className="fw-bold mb-1"><i className="bi bi-exclamation-triangle me-2"></i>Cancellation in Process</div>
      {order.cancellationReason && <div className="small">Reason: {order.cancellationReason}</div>}
      <div className="small text-muted">Refund will be processed within 5–7 business days.</div>
    </div>
  );

  if (s === "cancelled") return (
    <div className="alert alert-secondary border-0 rounded-3 mt-3 mb-0">
      <div className="fw-bold mb-1"><i className="bi bi-x-circle me-2"></i>Order Cancelled</div>
      {order.refundAmount && <div className="small">Refund: ₹{order.refundAmount} via {order.refundMethod}</div>}
    </div>
  );

  return null;
};

// Timeline for order history
const OrderTimeline = ({ order }) => {
  const steps = [
    { key: "createdAt",   label: "Order Placed",    icon: "bag-check",        color: "#6c757d" },
    { key: "confirmedAt", label: "Order Confirmed",  icon: "check-circle",     color: "#0dcaf0" },
    { key: "placedAt",    label: "Shipped",          icon: "truck",            color: "#0d6efd" },
    { key: "deliveredAt", label: "Delivered",        icon: "check-circle-fill",color: "#198754" },
  ];

  // For cancelled orders replace last step
  const isCancelled = ["cancelled", "cancellation_in_process"].includes(order.status);
  if (isCancelled) {
    steps.splice(3, 1,
      { key: "cancellationInitiatedAt", label: "Cancellation Initiated", icon: "x-circle", color: "#ffc107" },
      { key: "refundCompletedAt",       label: "Refund Completed",       icon: "currency-rupee", color: "#6c757d" }
    );
  }

  const doneKeys = steps.map((s) => s.key).filter((k) => order[k]);

  return (
    <div className="mt-3 px-2">
      {steps.map((step, i) => {
        const done = !!order[step.key];
        const isLast = i === steps.length - 1;
        return (
          <div key={step.key} className="d-flex gap-3" style={{ minHeight: isLast ? "auto" : 52 }}>
            {/* dot + line */}
            <div className="d-flex flex-column align-items-center" style={{ width: 24 }}>
              <div
                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: 24, height: 24,
                  background: done ? step.color : "#dee2e6",
                  color: "#fff", fontSize: 11,
                }}
              >
                <i className={`bi bi-${done ? step.icon : "circle"}`}></i>
              </div>
              {!isLast && (
                <div style={{ width: 2, flex: 1, background: done && doneKeys.includes(steps[i + 1]?.key) ? step.color : "#dee2e6", minHeight: 20 }} />
              )}
            </div>
            {/* label */}
            <div className="pb-3">
              <div className={`small fw-semibold ${done ? "" : "text-muted"}`}>{step.label}</div>
              {done && order[step.key] && (
                <div className="text-muted" style={{ fontSize: 11 }}>{fmt(order[step.key])}</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const OrderCard = ({ order, onViewBill, onRate, isHistory }) => {
  const [expanded, setExpanded] = useState(false);

  const statusLabel = {
    confirmed: { color: "info",      icon: "check-circle",      text: "Confirmed" },
    placed:    { color: "primary",   icon: "truck",             text: "Shipped" },
    delivered: { color: "success",   icon: "check-circle-fill", text: "Delivered" },
    cancellation_in_process: { color: "warning", icon: "x-circle", text: "Cancellation in Process" },
    cancelled: { color: "secondary", icon: "x-circle-fill",    text: "Cancelled" },
    pending:   { color: "warning",   icon: "clock",             text: "Pending" },
  };

  const info = statusLabel[order.status || "confirmed"] || statusLabel.confirmed;

  return (
    <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
      {/* Header strip */}
      <div className="px-4 py-2 d-flex justify-content-between align-items-center"
        style={{ background: "#f8f9fa", borderBottom: "1px solid #eee" }}>
        <span className="small text-muted font-monospace">#{(order.orderId || order.id)?.slice(0, 16)}</span>
        <span className={`badge bg-${info.color}`}>
          <i className={`bi bi-${info.icon} me-1`}></i>{info.text}
        </span>
      </div>

      <div className="card-body p-4">
        {/* Date + amount row */}
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="small text-muted">
            <i className="bi bi-calendar3 me-1"></i>
            {fmt(order.createdAt) || "—"}
          </div>
          <div className="fw-bold text-success fs-6">₹{Number(order.total || 0).toLocaleString()}</div>
        </div>

        {/* Items */}
        <div className="d-flex gap-2 flex-wrap mb-3">
          {order.items?.map((item, i) => (
            <div key={i} className="d-flex align-items-center gap-2 bg-light rounded-3 px-3 py-2">
              {(item.images?.[0] || item.image) && (
                <img src={item.images?.[0] || item.image} alt={item.name}
                  style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 6 }} />
              )}
              <div>
                <div className="small fw-semibold">{item.name}</div>
                <div className="text-muted" style={{ fontSize: 11 }}>
                  Qty: {item.quantity || item.qty} · ₹{item.price}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Delivery address */}
        {order.address && (
          <div className="small text-muted mb-3">
            <i className="bi bi-geo-alt me-1"></i>
            {order.address}, {order.city}, {order.state} – {order.pincode}
          </div>
        )}

        {/* History: show timeline; Active: show status card */}
        {isHistory ? (
          <>
            <button
              className="btn btn-link btn-sm p-0 text-decoration-none text-muted"
              onClick={() => setExpanded((v) => !v)}
            >
              <i className={`bi bi-chevron-${expanded ? "up" : "down"} me-1`}></i>
              {expanded ? "Hide" : "Show"} Order Timeline
            </button>
            {expanded && <OrderTimeline order={order} />}
          </>
        ) : (
          <StatusCard order={order} />
        )}

        {/* Rating already given */}
        {order.rating && (
          <div className="mt-3 p-3 bg-light rounded-3">
            <div className="small fw-bold mb-1">Your Rating</div>
            <Stars value={order.rating} size={18} />
            {order.review && <p className="small fst-italic mt-1 mb-0">"{order.review}"</p>}
          </div>
        )}

        {/* Actions */}
        <div className="d-flex gap-2 mt-3 flex-wrap">
          <button
            className="btn btn-outline-secondary btn-sm rounded-pill"
            onClick={() => onViewBill(order)}
          >
            <i className="bi bi-receipt me-1"></i>Invoice
          </button>
          {order.status === "delivered" && !order.rating && (
            <button
              className="btn btn-warning btn-sm rounded-pill"
              onClick={() => onRate(order)}
            >
              <i className="bi bi-star me-1"></i>Rate & Review
            </button>
          )}
          {order.trackingLink && (
            <a href={order.trackingLink} target="_blank" rel="noopener noreferrer"
              className="btn btn-outline-primary btn-sm rounded-pill">
              <i className="bi bi-truck me-1"></i>Track
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const MyOrders = () => {
  const dispatch = useDispatch();
  const rawOrders = useSelector((s) => s.orders.items || []);
  const ordersStatus = useSelector((s) => s.orders.status);
  const user = useSelector((s) => s.auth.user);
  const navigate = useNavigate();
  const [tab, setTab] = useState("active");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showBill, setShowBill] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState("");
  const [hover, setHover] = useState(0);

  // rawOrders is already filtered by userId in ordersSlice
  const orders = Array.isArray(rawOrders)
    ? [...rawOrders].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    : [];

  const activeOrders  = orders.filter((o) => ["pending", "confirmed", "placed"].includes(o.status || "confirmed"));
  const historyOrders = orders.filter((o) => ["delivered", "cancelled", "cancellation_in_process"].includes(o.status));

  useEffect(() => {
    if (user?.uid) dispatch(fetchOrders(user.uid));
  }, [dispatch, user]);

  if (!user) { navigate("/signin"); return null; }

  const loading = ordersStatus === "loading";

  const handleSubmitRating = async () => {
    if (!rating) { toast.error("Please select a rating"); return; }
    try {
      const updated = {
        ...selectedOrder, rating, review, ratedAt: Date.now(), showRatingPopup: false,
        name: user?.name || selectedOrder.name,
        photoURL: user?.photoURL || null,
      };
      if (db) await update(ref(db, `orders/${selectedOrder.id}`), updated);
      await dispatch(updateOrder({ id: selectedOrder.id, data: updated })).unwrap();
      toast.success("Thank you for your feedback!");
      setShowRatingModal(false);
      setRating(0);
      setReview("");
    } catch {
      toast.error("Failed to submit rating");
    }
  };

  const totalSpent = historyOrders
    .filter((o) => o.status === "delivered")
    .reduce((sum, o) => sum + (o.total || 0), 0);

  return (
    <div className="py-5" style={{ background: "#f8f9fa", minHeight: "100vh" }}>
      <div className="container" style={{ maxWidth: 800 }}>
        <h1 className="fw-bold mb-4"><i className="bi bi-box-seam me-2"></i>My Orders</h1>

        {/* STATS */}
        <div className="row g-3 mb-4">
          {[
            { label: "Active Orders",    value: activeOrders.length,  icon: "hourglass-split", grad: "135deg,#667eea,#764ba2" },
            { label: "Delivered Orders", value: historyOrders.filter(o => o.status === "delivered").length, icon: "check-circle", grad: "135deg,#10b981,#059669" },
            { label: "Total Spent",      value: `₹${totalSpent.toLocaleString()}`, icon: "currency-rupee", grad: "135deg,#f59e0b,#d97706" },
          ].map((s) => (
            <div key={s.label} className="col-4">
              <div className="card border-0 shadow-sm rounded-4 p-2 p-md-3 h-100"
                style={{ background: `linear-gradient(${s.grad})`, color: "white" }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="min-w-0">
                    <p className="orders-stat-label mb-1 opacity-75" style={{ fontSize: "0.7rem" }}>{s.label}</p>
                    <h5 className="orders-stat-value fw-bold mb-0" style={{ fontSize: "1rem" }}>{s.value}</h5>
                  </div>
                  <i className={`bi bi-${s.icon} d-none d-md-block`} style={{ fontSize: 24, opacity: 0.7 }}></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* TABS */}
        <ul className="nav nav-pills mb-4 gap-2">
          <li className="nav-item">
            <button
              className={`nav-link rounded-pill px-4 ${tab === "active" ? "active" : "text-dark"}`}
              style={tab === "active" ? { background: "#2f0f0c" } : { background: "#fff", border: "1px solid #dee2e6" }}
              onClick={() => setTab("active")}
            >
              <i className="bi bi-hourglass-split me-2"></i>Active
              {activeOrders.length > 0 && (
                <span className="badge bg-warning text-dark ms-2">{activeOrders.length}</span>
              )}
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link rounded-pill px-4 ${tab === "history" ? "active" : "text-dark"}`}
              style={tab === "history" ? { background: "#2f0f0c" } : { background: "#fff", border: "1px solid #dee2e6" }}
              onClick={() => setTab("history")}
            >
              <i className="bi bi-clock-history me-2"></i>Order History
              {historyOrders.length > 0 && (
                <span className="badge bg-secondary ms-2">{historyOrders.length}</span>
              )}
            </button>
          </li>
        </ul>

        {/* ACTIVE ORDERS */}
        {tab === "active" && (
          loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" style={{ color: "#2f0f0c" }} role="status" />
              <p className="text-muted mt-3">Loading your orders...</p>
            </div>
          ) : activeOrders.length === 0 ? (
            <div className="card border-0 shadow-sm rounded-4 p-5 text-center">
              <i className="bi bi-bag-check display-4 d-block mb-3 text-muted"></i>
              <h5 className="text-muted">No active orders</h5>
              <p className="text-muted small">All your orders have been delivered or cancelled.</p>
              <Link to="/perfumes" className="btn btn-dark mt-3 rounded-pill px-4"
                style={{ background: "#2f0f0c" }}>
                <i className="bi bi-shop me-2"></i>Shop Now
              </Link>
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {activeOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  isHistory={false}
                  onViewBill={(o) => { setSelectedOrder(o); setShowBill(true); }}
                  onRate={(o) => { setSelectedOrder(o); setShowRatingModal(true); }}
                />
              ))}
            </div>
          )
        )}

        {/* ORDER HISTORY */}
        {tab === "history" && (
          loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" style={{ color: "#2f0f0c" }} role="status" />
              <p className="text-muted mt-3">Loading your orders...</p>
            </div>
          ) : historyOrders.length === 0 ? (
            <div className="card border-0 shadow-sm rounded-4 p-5 text-center">
              <i className="bi bi-clock-history display-4 d-block mb-3 text-muted"></i>
              <h5 className="text-muted">No order history yet</h5>
              <p className="text-muted small">Completed and cancelled orders will appear here.</p>
              <Link to="/perfumes" className="btn btn-dark mt-3 rounded-pill px-4"
                style={{ background: "#2f0f0c" }}>
                <i className="bi bi-shop me-2"></i>Shop Now
              </Link>
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {historyOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  isHistory={true}
                  onViewBill={(o) => { setSelectedOrder(o); setShowBill(true); }}
                  onRate={(o) => { setSelectedOrder(o); setShowRatingModal(true); }}
                />
              ))}
            </div>
          )
        )}
      </div>

      {/* Bill Modal */}
      {showBill && selectedOrder && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Invoice</h5>
                <button className="btn-close" onClick={() => setShowBill(false)} />
              </div>
              <div className="modal-body">
                <Bill order={selectedOrder} onDownload={() => downloadBillAsPDF(selectedOrder.orderId || selectedOrder.id)} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rating Modal */}
      {showRatingModal && selectedOrder && (
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-star-fill text-warning me-2"></i>Rate Your Experience
                </h5>
                <button className="btn-close" onClick={() => { setShowRatingModal(false); setRating(0); setReview(""); }} />
              </div>
              <div className="modal-body text-center">
                <p className="text-muted mb-4">How was your experience with this order?</p>
                <div className="mb-4">
                  <Stars value={rating} onChange={setRating} hover={hover} onHover={setHover} onLeave={() => setHover(0)} size={48} />
                </div>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Share your experience (optional)"
                  value={review}
                  onChange={(e) => setReview(e.target.value)}
                />
              </div>
              <div className="modal-footer border-0">
                <button className="btn btn-secondary" onClick={() => { setShowRatingModal(false); setRating(0); setReview(""); }}>Skip</button>
                <button className="btn btn-primary" onClick={handleSubmitRating} disabled={!rating}>
                  <i className="bi bi-check-circle me-1"></i>Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyOrders;
